import React, { useState } from "react";
import Card from "components/card";
import SearchIcon from "components/icons/SearchIcon";
import { MdChevronRight, MdChevronLeft, MdVisibility, MdCheckCircle, MdBlock } from "react-icons/md";
import { UserReportData } from "../data";
import ConfirmPopup from "components/popup/ConfirmPopup";

import {
  PaginationState,
  createColumnHelper,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<UserReportData>();

function UserReportsTable(props: { tableData: UserReportData[] }) {
  const { tableData } = props;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedReport, setSelectedReport] = useState<UserReportData | null>(null);
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });

  const getStatusBadge = (status: string) => {
    return status === "Resolved"
      ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300";
  };

  const handleReportAction = (reportId: string, action: string) => {
    console.log(`${action} report ${reportId}`);
    // Handle the action
  };

  const handleDisableUser = (userId: string, userName: string) => {
    setConfirmConfig({
      isOpen: true,
      message: `Are you sure you want to disable ${userName}? This will prevent them from accessing the platform.`,
      title: 'Disable User',
      onConfirm: () => {
        console.log(`Disabling user ${userId}`);
        // Handle user disabling
      },
    });
  };

  const createPages = (count: number) => {
    let arrPageCount = [];
    for (let i = 1; i <= count; i++) {
      arrPageCount.push(i);
    }
    return arrPageCount;
  };

  const columns = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">REPORT ID</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-brand-500 dark:text-brand-400">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("reportedUser", {
      id: "reportedUser", 
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">REPORTED USER</p>
      ),
      cell: (info) => {
        const user = info.getValue();
        return (
          <div>
            <p className="text-sm font-medium text-navy-700 dark:text-white">
              {user.name}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {user.type} • {user.email}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("reportType", {
      id: "reportType",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">TYPE</p>
      ),
      cell: (info) => (
        <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.display({
      id: "submitter",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">SUBMITTED BY</p>
      ),
      cell: (info) => {
        const report = info.row.original;
        const submitter = report.submitter;
        return (
          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
            submitter === "LLM" 
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
              : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
          }`}>
            {submitter}
          </span>
        );
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">STATUS</p>
      ),
      cell: (info) => (
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(info.getValue())}`}>
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("dateReported", {
      id: "dateReported",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">DATE</p>
      ),
      cell: (info) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ACTIONS</p>
      ),
      cell: (info) => {
        const report = info.row.original;
        return (
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedReport(report)}
              className="rounded bg-blue-500 p-2 text-white hover:bg-blue-600 transition duration-200"
              title="View Details"
            >
              <MdVisibility className="h-4 w-4" />
            </button>
            {report.status === "Pending" && (
              <button
                onClick={() => handleReportAction(report.id, "resolve")}
                className="rounded bg-green-500 p-2 text-white hover:bg-green-600 transition duration-200"
                title="Mark as Resolved"
              >
                <MdCheckCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      },
    }),
  ];

  const [data, setData] = useState(() => [...tableData]);
  
  // Force re-render when tableData changes
  React.useEffect(() => {
    setData([...tableData]);
  }, [tableData]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <Card extra={"w-full h-full sm:overflow-auto p-0"}>
        {/* Header Section */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 px-6 py-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage and review user reports ({table.getFilteredRowModel().rows.length} total reports)
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="flex h-[42px] w-[320px] items-center rounded-lg border border-gray-200 bg-white px-3 dark:border-gray-600 dark:bg-navy-800">
            <SearchIcon />
            <input
              value={globalFilter ?? ""}
              onChange={(e: any) => setGlobalFilter(e.target.value)}
              type="text"
              placeholder="Search reports..."
              className="ml-2 block w-full bg-transparent text-sm text-navy-700 placeholder-gray-500 outline-none dark:text-white dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Cards Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {table
              .getRowModel()
              .rows.map((row) => {
                const report = row.original;
                return (
                  <div
                    key={report.id}
                    className="group rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-all duration-200 dark:border-gray-600 dark:bg-navy-800"
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          report.submitter === "LLM" 
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                        }`}>
                          {report.submitter}
                        </span>
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(report.status)}`}>
                          {report.status}
                        </span>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="View Details"
                        >
                          <MdVisibility className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        {report.status === "Pending" && (
                          <button
                            onClick={() => handleReportAction(report.id, "resolve")}
                            className="rounded p-1 hover:bg-green-100 dark:hover:bg-green-900/20"
                            title="Mark as Resolved"
                          >
                            <MdCheckCircle className="h-4 w-4 text-green-500" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Report ID */}
                    <p className="text-xs font-mono text-brand-500 dark:text-brand-400 mb-2">
                      {report.id}
                    </p>

                    {/* Report Type */}
                    <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300 mb-3">
                      {report.reportType}
                    </span>

                    {/* Reported User */}
                    <div className="mb-3">
                      <p className="text-sm font-medium text-navy-700 dark:text-white">
                        {report.reportedUser.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {report.reportedUser.type} • {report.reportedUser.email}
                      </p>
                    </div>

                    {/* Description Preview */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                      {report.description}
                    </p>

                    {/* Date and Actions */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {report.dateReported}
                      </span>
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="rounded bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 transition duration-200"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* pagination */}
        <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-navy-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-medium text-navy-700 dark:text-white">{table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}</span> to <span className="font-medium text-navy-700 dark:text-white">{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}</span> of <span className="font-medium text-navy-700 dark:text-white">{table.getFilteredRowModel().rows.length}</span> results
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition duration-200 ${
                  !table.getCanPreviousPage() 
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                }`}
              >
                <MdChevronLeft className="text-lg" />
              </button>

              <div className="flex items-center gap-1">
                {createPages(table.getPageCount()).slice(
                  Math.max(0, pageIndex - 2),
                  Math.min(table.getPageCount(), pageIndex + 3)
                ).map((pageNumber, index) => {
                  return (
                    <button
                      className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition duration-200 ${
                        pageNumber === pageIndex + 1
                          ? "bg-brand-500 text-white shadow-sm hover:bg-brand-600"
                          : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                      }`}
                      onClick={() => table.setPageIndex(pageNumber - 1)}
                      key={index}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition duration-200 ${
                  !table.getCanNextPage()
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                }`}
              >
                <MdChevronRight className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Report Details Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-navy-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">Report Details</h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Report ID</p>
                  <p className="text-sm text-navy-700 dark:text-white">{selectedReport.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Date Reported</p>
                  <p className="text-sm text-navy-700 dark:text-white">{selectedReport.dateReported}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reported User</p>
                  <p className="text-sm text-navy-700 dark:text-white">{selectedReport.reportedUser.name}</p>
                  <p className="text-xs text-gray-500">{selectedReport.reportedUser.type} • {selectedReport.reportedUser.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Submitted By</p>
                  {selectedReport.reportedBy ? (
                    <>
                      <p className="text-sm text-navy-700 dark:text-white">{selectedReport.reportedBy.name}</p>
                      <p className="text-xs text-gray-500">{selectedReport.reportedBy.type} • {selectedReport.reportedBy.email}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-navy-700 dark:text-white">AI Detection System</p>
                      <p className="text-xs text-gray-500">Automated content moderation</p>
                    </>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Report Type</p>
                  <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
                    {selectedReport.reportType}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Detection Method</p>
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                    selectedReport.submitter === "LLM" 
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                  }`}>
                    {selectedReport.submitter === "LLM" ? "AI Detection" : "User Report"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(selectedReport.status)}`}>
                    {selectedReport.status}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Description</p>
                <p className="text-sm text-navy-700 dark:text-white bg-gray-50 dark:bg-navy-700 p-3 rounded">
                  {selectedReport.description}
                </p>
              </div>
              
              
              <div className="flex justify-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                {selectedReport.status === "Pending" && (
                  <button
                    disabled
                    title="Coming Soon"
                    onClick={() => {
                      handleReportAction(selectedReport.id, "resolve");
                      setSelectedReport(null);
                    }}
                    className="flex items-center gap-2 rounded bg-green-500 px-6 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50 disabled:hover:bg-green-500"
                  >
                    <MdCheckCircle className="h-4 w-4" />
                    Mark as Resolved
                  </button>
                )}
                <button
                  disabled
                  title="Coming Soon"
                  onClick={() => {
                    handleDisableUser(selectedReport.reportedUser.email, selectedReport.reportedUser.name);
                    setSelectedReport(null);
                  }}
                  className="flex items-center gap-2 rounded bg-red-500 px-6 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 disabled:hover:bg-red-500"
                >
                  <MdBlock className="h-4 w-4" />
                  Disable User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Popup */}
      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Disable"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
}

export default UserReportsTable;